import { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Avatar from '@mui/material/Avatar';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import { ROUTES } from '../../constants/routes';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useGetMembershipSettingsQuery } from '../../store/siteContentApi';
import { useListBookingsQuery } from '../../store/bookingsApi';
import useDetectedCity from '../../hooks/useDetectedCity';
import Logo from '../common/Logo';
import ProfileDrawer from './ProfileDrawer';
import NotificationBell from './NotificationBell';

// "My Reports" picks its destination based on session state so the
// click feels instant (one route change, no flicker):
//   ─ Patient who has a UHID AND already verified it this session
//     → /dashboard
//   ─ Partner (referring doctor) who already verified credentials
//     → /portal/medical
//   ─ Hospital / lab account who already verified credentials
//     → /portal/lab
//   ─ Organization (corporate) account who already verified credentials
//     → /portal/corporate
//   ─ Anyone else (not verified)
//     → /reports (the role picker)
// Reports.js still does its own gating, so this is purely a UX
// shortcut that avoids the /reports → /portal/* double-hop.
const reportsRouteFor = (user, uhidVerified) => {
  if (!uhidVerified) return ROUTES.REPORTS;
  if (user?.user_type === 'patient' && user?.uhid) return ROUTES.DASHBOARD;
  if (user?.user_type === 'partner')               return ROUTES.MED_DASHBOARD;
  if (user?.user_type === 'hospital')              return ROUTES.LAB_DASHBOARD;
  if (user?.user_type === 'organization')          return ROUTES.CORP_DASHBOARD;
  return ROUTES.REPORTS;
};

// Profile-menu variant of `reportsRouteFor`. A first-time patient who
// hasn't received their UHID yet has nowhere useful to go from the
// Reports portal — it would just gate them and bounce. Send them to
// their most recent booking's tracking page instead, which works
// without a UHID and shows the "WhatsApp UHID + password" nudge so
// they understand what they're waiting for.
//
// IMPORTANT: only the profile-dropdown menu item uses this. The pill
// button and mobile drawer keep using `reportsRouteFor` per spec.
const reportsRouteForProfileMenu = (user, uhidVerified, latestBookingId) => {
  if (
    user?.user_type === 'patient' &&
    !user?.uhid &&
    latestBookingId
  ) {
    return `${ROUTES.TRACK}?id=${encodeURIComponent(latestBookingId)}`;
  }
  return reportsRouteFor(user, uhidVerified);
};

const LINKS = [
  { to: ROUTES.HOME, label: 'Home', end: true },
  { to: ROUTES.TESTS, label: 'Tests' },
  { to: ROUTES.PACKAGES, label: 'Checkups' },
  { to: ROUTES.CORPORATE, label: 'Corporate' },
  { to: ROUTES.SPECIALS, label: 'Offers' },
  { to: ROUTES.MEMBERSHIP, label: 'Membership' },
  { to: ROUTES.ABOUT, label: 'About' },
  { to: ROUTES.BLOG, label: 'Blog' },
  { to: ROUTES.SUPPORT, label: 'Support' },
];

const Navbar = () => {
  const navigate = useNavigate();
  const { totals } = useCart();
  const { user, isAuthenticated, uhidVerified, logout } = useAuth();
  const { data: membershipSettings } = useGetMembershipSettingsQuery();
  const membershipEnabled = membershipSettings?.is_enabled ?? false;
  // Pre-fetch the user's bookings so the profile-menu redirect for a
  // first-time patient (no UHID → /track?id=<latest>) feels instant.
  // Skipped when not signed in. The list endpoint already excludes
  // `awaiting_payment` rows server-side, so [0] is a real booking.
  const { data: myBookingsData } = useListBookingsQuery(undefined, { skip: !isAuthenticated });
  const latestBookingId = myBookingsData?.bookings?.[0]?.booking_id || null;
  const { city: detectedCity, status: cityStatus, refresh: refreshCity } = useDetectedCity();
  // Filter out the Membership link when the feature flag is off — treats
  // `undefined` (still loading / fetch failed) as OFF to avoid flicker.
  const visibleLinks = LINKS.filter((l) => l.to !== ROUTES.MEMBERSHIP || membershipEnabled);
  const [open, setOpen] = useState(false);
  const [profileAnchor, setProfileAnchor] = useState(null);
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);

  const openProfile = (e) => setProfileAnchor(e.currentTarget);
  const closeProfile = () => setProfileAnchor(null);
  const openProfileDrawer = () => { closeProfile(); setProfileDrawerOpen(true); };
  const closeProfileDrawer = () => setProfileDrawerOpen(false);

  const initials = (user?.name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('') || '';

  const handleLogout = () => {
    closeProfile();
    logout();
    navigate(ROUTES.HOME);
  };

  return (
    <AppBar
      position="sticky"
      sx={{
        bgcolor: 'rgba(250,246,239,0.97)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid var(--border)',
        color: 'var(--ink)',
      }}
    >
      <Toolbar
        sx={{
          px: { xs: 2, md: 7 },
          py: 1,
          minHeight: { xs: 64, md: 80 },
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Logo onClick={() => navigate(ROUTES.HOME)} size="lg" />

        <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 0.5 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={refreshCity}
            startIcon={<LocationOnOutlinedIcon />}
            title={cityStatus === 'detecting' ? 'Detecting location…' : 'Tap to refresh location'}
            sx={{ borderRadius: 100, mr: 1, fontSize: 13, color: 'var(--charcoal)' }}
          >
            {cityStatus === 'detecting' ? 'Detecting…' : detectedCity}
          </Button>
          {visibleLinks.map((link) => (
            <Button
              key={link.to}
              component={NavLink}
              to={link.to}
              end={link.end}
              disableRipple
              sx={{
                color: 'var(--stone)',
                fontWeight: 600,
                fontSize: 13,
                textTransform: 'none',
                borderRadius: 0,
                px: 1.75,
                py: 1,
                position: 'relative',
                bgcolor: 'transparent',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  left: 14,
                  right: 14,
                  bottom: 4,
                  height: '2px',
                  bgcolor: 'var(--sage)',
                  borderRadius: 1,
                  transform: 'scaleX(0)',
                  transformOrigin: 'center',
                  transition: 'transform 0.2s ease',
                },
                '&.active': {
                  color: 'var(--sage)',
                  fontWeight: 700,
                  '&::after': { transform: 'scaleX(1)' },
                },
                '&:hover': { color: 'var(--sage)', bgcolor: 'transparent' },
              }}
            >
              {link.label}
            </Button>
          ))}
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          {/* 1. My Reports (pill with text, only when authenticated) */}
          {isAuthenticated && (
            <Button
              component={Link}
              to={reportsRouteFor(user, uhidVerified)}
              startIcon={<DescriptionOutlinedIcon sx={{ fontSize: 18 }} />}
              sx={{
                display: { xs: 'none', md: 'inline-flex' },
                color: 'var(--charcoal)',
                border: '1px solid var(--border)',
                borderRadius: 100,
                px: 1.75,
                height: 36,
                fontSize: 13,
                fontWeight: 600,
                textTransform: 'none',
                lineHeight: 1,
                '&:hover': { borderColor: 'var(--sage)', color: 'var(--sage)', bgcolor: 'transparent' },
              }}
            >
              My Reports
            </Button>
          )}

          {/* 2. Notifications bell — only when authed (component self-guards) */}
          <NotificationBell />

          {/* 3. Cart — icon only, matches the bell's round chip styling */}
          <IconButton
            component={Link}
            to={ROUTES.CART}
            aria-label="Cart"
            sx={{
              color: 'var(--charcoal)',
              border: '1px solid var(--border)',
              borderRadius: '50%',
              width: 36, height: 36, p: 0,
              '&:hover': { borderColor: 'var(--sage)', color: 'var(--sage)' },
            }}
          >
            <Badge
              badgeContent={totals.count}
              color="secondary"
              sx={{ '& .MuiBadge-badge': { fontSize: 10, fontWeight: 800, top: 2, right: 2 } }}
            >
              <ShoppingCartOutlinedIcon sx={{ fontSize: 18 }} />
            </Badge>
          </IconButton>

          {/* 3. Profile (round avatar — anchors the right edge) */}
          <IconButton
            onClick={openProfile}
            aria-label="Account menu"
            sx={{
              color: 'var(--charcoal)',
              border: '1px solid var(--border)',
              borderRadius: '50%',
              p: 0,
              width: 36,
              height: 36,
              '&:hover': { borderColor: 'var(--sage)', color: 'var(--sage)' },
            }}
          >
            {isAuthenticated ? (
              <Avatar
                sx={{
                  width: 30, height: 30,
                  fontSize: 12, fontWeight: 700,
                  bgcolor: 'var(--sage)', color: '#fff',
                }}
              >
                {initials || <PersonOutlineIcon sx={{ fontSize: 18 }} />}
              </Avatar>
            ) : (
              <PersonOutlineIcon sx={{ fontSize: 20 }} />
            )}
          </IconButton>

          <Menu
            anchorEl={profileAnchor}
            open={Boolean(profileAnchor)}
            onClose={closeProfile}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              paper: {
                sx: {
                  mt: 1,
                  minWidth: 200,
                  borderRadius: '12px',
                  border: '1px solid var(--border-l)',
                  boxShadow: '0 10px 28px rgba(21,42,30,0.08)',
                },
              },
            }}
          >
            {isAuthenticated ? (
              [
                <Box key="hdr" sx={{ px: 2, py: 1.25 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)', lineHeight: 1.2 }}>
                    {user?.name || 'User'}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: 'var(--stone)', textTransform: 'capitalize' }}>
                    {String(user?.user_type || '').replace('_', ' ')}
                  </Typography>
                </Box>,
                <Divider key="div1" />,
                <MenuItem key="profile" onClick={openProfileDrawer}>
                  <AccountCircleOutlinedIcon sx={{ fontSize: 18, mr: 1.25, color: 'var(--stone)' }} />
                  <Typography sx={{ fontSize: 14 }}>Profile</Typography>
                </MenuItem>,
                <MenuItem
                  key="reports"
                  onClick={() => {
                    closeProfile();
                    navigate(reportsRouteForProfileMenu(user, uhidVerified, latestBookingId));
                  }}
                >
                  <DescriptionOutlinedIcon sx={{ fontSize: 18, mr: 1.25, color: 'var(--stone)' }} />
                  <Typography sx={{ fontSize: 14 }}>My Reports</Typography>
                </MenuItem>,
                <MenuItem
                  key="tickets"
                  onClick={() => { closeProfile(); navigate(ROUTES.MY_TICKETS); }}
                >
                  <SupportAgentOutlinedIcon sx={{ fontSize: 18, mr: 1.25, color: 'var(--stone)' }} />
                  <Typography sx={{ fontSize: 14 }}>My Tickets</Typography>
                </MenuItem>,
                <Divider key="div2" />,
                <MenuItem key="logout" onClick={handleLogout}>
                  <LogoutOutlinedIcon sx={{ fontSize: 18, mr: 1.25, color: 'var(--stone)' }} />
                  <Typography sx={{ fontSize: 14 }}>Logout</Typography>
                </MenuItem>,
              ]
            ) : (
              [
                <MenuItem key="login" onClick={() => { closeProfile(); navigate(ROUTES.LOGIN); }}>
                  <LoginOutlinedIcon sx={{ fontSize: 18, mr: 1.25, color: 'var(--stone)' }} />
                  <Typography sx={{ fontSize: 14 }}>Login</Typography>
                </MenuItem>,
                <MenuItem key="signup" onClick={() => { closeProfile(); navigate(ROUTES.SIGNUP); }}>
                  <PersonOutlineIcon sx={{ fontSize: 18, mr: 1.25, color: 'var(--stone)' }} />
                  <Typography sx={{ fontSize: 14 }}>Sign up</Typography>
                </MenuItem>,
              ]
            )}
          </Menu>

          {/* 4. Mobile hamburger (rightmost) */}
          <IconButton
            sx={{ display: { xs: 'inline-flex', lg: 'none' }, ml: 0.5 }}
            onClick={() => setOpen(true)}
            aria-label="Open menu"
          >
            <MenuIcon />
          </IconButton>
        </Stack>
      </Toolbar>

      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <Box sx={{ width: 280, p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Logo size="sm" />
            <IconButton onClick={() => setOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <List>
            {visibleLinks.map((link) => (
              <ListItemButton
                key={link.to}
                component={NavLink}
                to={link.to}
                end={link.end}
                onClick={() => setOpen(false)}
              >
                <ListItemText
                  primary={link.label}
                  primaryTypographyProps={{ fontWeight: 600, fontSize: 15 }}
                />
              </ListItemButton>
            ))}
            <ListItemButton component={Link} to={ROUTES.CART} onClick={() => setOpen(false)}>
              <ListItemText primary={`Cart (${totals.count})`} primaryTypographyProps={{ fontWeight: 600, fontSize: 15 }} />
            </ListItemButton>
            {isAuthenticated ? (
              <>
                <ListItemButton onClick={() => { setOpen(false); openProfileDrawer(); }}>
                  <ListItemText
                    primary={`Profile · ${user?.name?.split(' ')[0] || 'You'}`}
                    primaryTypographyProps={{ fontWeight: 600, fontSize: 15 }}
                  />
                </ListItemButton>
                <ListItemButton
                  component={Link}
                  to={reportsRouteFor(user, uhidVerified)}
                  onClick={() => setOpen(false)}
                >
                  <ListItemText primary="My Reports" primaryTypographyProps={{ fontWeight: 600, fontSize: 15 }} />
                </ListItemButton>
                <ListItemButton onClick={() => { setOpen(false); handleLogout(); }}>
                  <ListItemText primary="Logout" primaryTypographyProps={{ fontWeight: 600, fontSize: 15, color: 'var(--red, #c0392b)' }} />
                </ListItemButton>
              </>
            ) : (
              <>
                <ListItemButton component={Link} to={ROUTES.LOGIN} onClick={() => setOpen(false)}>
                  <ListItemText primary="Login" primaryTypographyProps={{ fontWeight: 600, fontSize: 15 }} />
                </ListItemButton>
                <ListItemButton component={Link} to={ROUTES.SIGNUP} onClick={() => setOpen(false)}>
                  <ListItemText primary="Sign up" primaryTypographyProps={{ fontWeight: 600, fontSize: 15 }} />
                </ListItemButton>
              </>
            )}
          </List>
        </Box>
      </Drawer>

      {/* Right-side profile drawer (only relevant when authenticated) */}
      <ProfileDrawer open={profileDrawerOpen} onClose={closeProfileDrawer} />
    </AppBar>
  );
};

export default Navbar;
